// SMART UI shared components (God UI/UX mandate 2026-07-25).
// Composable primitives that ride on src/ui/smart.css — designed to drop into
// the existing inline-style pages without restyling them wholesale.
import React, { useState } from 'react';

/** Skeleton loader block — replaces spinners. */
export function Skel({ w = '100%', h = 14, style }: {
  w?: number | string; h?: number | string; style?: React.CSSProperties;
}) {
  return <div className="skel" style={{ width: w, height: h, ...style }} />;
}

/** A stack of skeleton rows for table/card loading states. */
export function SkelRows({ rows = 3, h = 52 }: { rows?: number; h?: number }) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <Skel key={i} h={h} style={{ marginBottom: 10 }} />
      ))}
    </div>
  );
}

/** Animated utilization meter with threshold ticks + status color.
 *  Status is ALSO shown as a text label — never color alone. */
export function MeterBar({ pct, ticks = [40, 70], height = 10 }: {
  pct: number; ticks?: number[]; height?: number;
}) {
  const p = Math.max(0, Math.min(100, pct || 0));
  const color = p >= (ticks[1] ?? 70) ? 'var(--jc-kill)'
    : p >= (ticks[0] ?? 40) ? 'var(--jc-warn)' : 'var(--jc-live)';
  return (
    <div className="meter" style={{ height }}>
      <div className="meter-fill" style={{ width: `${p}%`, backgroundColor: color }} />
      {ticks.map(t => (
        <div key={t} className="meter-tick" style={{ left: `${t}%` }} />
      ))}
    </div>
  );
}

/** Quick-search input for tables/lists. */
export function SearchBar({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div className="qsearch">
      <span style={{ color: 'var(--jc-ink-3)', fontSize: 13 }}>🔎</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Search…'}
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="ripple"
          style={{ background: 'transparent', border: 0, color: 'var(--jc-ink-3)',
                   cursor: 'pointer', fontSize: 13 }}
          aria-label="Clear search"
        >✕</button>
      )}
    </div>
  );
}

/** Slim glass inline alert — informs, doesn't scream. Long explanations go
 *  in `detail` (collapsed <details>), so honesty text stays reachable. */
export function InlineAlert({ tone = 'info', icon, children, detail, detailLabel }: {
  tone?: 'info' | 'ok' | 'warn' | 'danger';
  icon?: React.ReactNode;
  children: React.ReactNode;
  detail?: React.ReactNode;
  detailLabel?: string;
}) {
  const fallback = tone === 'danger' ? '⛔' : tone === 'warn' ? '⚠️' : tone === 'ok' ? '✅' : 'ℹ️';
  return (
    <div className={`alert-slim ${tone}`}>
      <span style={{ fontSize: 11, lineHeight: '15px' }}>{icon ?? fallback}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>{children}</div>
        {detail && (
          <details>
            <summary>{detailLabel || 'wajah + fix →'}</summary>
            <div className="alert-detail">{detail}</div>
          </details>
        )}
      </div>
    </div>
  );
}

/** Collapsible step section (Edit Bot step-by-step matrix). */
export function AccSection({ step, title, summary, open, onToggle, children }: {
  step?: string; title: string; summary?: React.ReactNode; open: boolean;
  onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className={`acc-section ${open ? 'open' : ''}`}>
      <button type="button" className="acc-head ripple" onClick={onToggle}>
        <span>
          {step && (
            <span style={{ color: 'var(--jc-accent)', marginRight: 8 }}>{step}</span>
          )}
          {title}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span className="acc-sum">{summary}</span>
          <span className="acc-caret">▾</span>
        </span>
      </button>
      <div className="acc-body"><div className="acc-inner">{children}</div></div>
    </div>
  );
}

/** Hook: single-open accordion state. */
export function useAccordion(initial: string) {
  const [open, setOpen] = useState<string>(initial);
  return {
    isOpen: (k: string) => open === k,
    toggle: (k: string) => setOpen(o => (o === k ? '' : k)),
    openSection: (k: string) => setOpen(k),
  };
}

/** Key-value row inside a mobile card. */
export function MRow({ k, children, mono }: {
  k: string; children: React.ReactNode; mono?: boolean;
}) {
  return (
    <div className="m-row">
      <span className="k">{k}</span>
      <span className={mono ? 'num' : undefined} style={{ textAlign: 'right', minWidth: 0 }}>
        {children}
      </span>
    </div>
  );
}
